import { createSHA256 } from 'hash-wasm';
import mediaFormats from '../../shared/mediaFormats.json';
const {
  DATASET_CONSENT_TEXT,
  DATASET_CONSENT_VERSION,
  DATASET_PRIVACY_VERSION,
  DATASET_TERMS_VERSION,
  MULTIPART_PART_BYTES,
} = require('../../shared/datasetConfig.json');

export { MULTIPART_PART_BYTES };

function readBlob(blob) {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

function extension(file) {
  return file.name?.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] || 'bin';
}

const lossless = new Set(['flac', 'wav', 'wave', 'aif', 'aiff', 'aifc', 'alac', 'ape']);
const lossy = new Set([
  'mp3', 'mp2', 'mpga', 'm2a', 'm3a', 'm4a', 'm4b', 'aac', 'adts',
  'ogg', 'oga', 'opus', 'webm', 'weba', 'wma', 'amr', 'ac3', 'eac3', 'spx', 'dts',
]);
const canonicalMimeByExtension = Object.freeze({ ...mediaFormats.canonicalAudioMimeByExtension });

export async function checksumSha256(file) {
  const hasher = await createSHA256();
  for (let start = 0; start < file.size; start += MULTIPART_PART_BYTES) {
    const chunk = file.slice(start, Math.min(start + MULTIPART_PART_BYTES, file.size));
    hasher.update(new Uint8Array(await readBlob(chunk)));
  }
  return hasher.digest('hex');
}

export async function buildDatasetMetadata(file, details = {}) {
  const ext = extension(file);
  return {
    declaredLanguage: 'ca',
    consentAccepted: true,
    adultConfirmed: true,
    speakerPermissionsConfirmed: true,
    sensitiveContentExcluded: true,
    termsVersion: DATASET_TERMS_VERSION,
    privacyVersion: DATASET_PRIVACY_VERSION,
    consentVersion: DATASET_CONSENT_VERSION,
    consentText: DATASET_CONSENT_TEXT,
    byteSize: file.size,
    // Browser and OS combinations report inconsistent MIME types for the same
    // audio extension (notably empty/octet-stream/x-hx-aac-adts for .aac).
    mimeType: canonicalMimeByExtension[ext] || file.type,
    extension: ext,
    checksumSha256: await checksumSha256(file),
    durationSeconds: Number.isFinite(details.durationSeconds) ? details.durationSeconds : null,
    sourceKind: details.sourceKind || 'audio',
    codec: details.codec || ({ mp3: 'mp3', flac: 'flac', opus: 'opus', aac: 'aac', alac: 'alac' }[ext] || 'unknown'),
    container: details.container || ext,
    encodingQuality: details.encodingQuality || (lossless.has(ext) ? 'lossless' : lossy.has(ext) ? 'lossy' : 'unknown'),
    channels: Number.isFinite(details.channels) ? details.channels : null,
    sampleRate: Number.isFinite(details.sampleRate) ? details.sampleRate : null,
    bitrate: Number.isFinite(details.bitrate) ? details.bitrate : (
      Number.isFinite(details.durationSeconds) && details.durationSeconds > 0
        ? Math.round((file.size * 8) / details.durationSeconds)
        : null
    ),
  };
}
